import { useOrderingMessage } from "../useOrderingMessage";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  ShoppingCartOutlined,
  CheckOutlined,
  MinusCircleOutlined,
  ScissorOutlined,
  TeamOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  PercentageOutlined,
  ToolOutlined,
  UndoOutlined,
  EditOutlined,
  UserSwitchOutlined,
  TagOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { CloseTableModal } from "./payment/CloseTableModal";
import { useClosingBills } from "./payment/closingBills";
import { RenameTableModal } from "./manage/RenameTableModal";
import {
  CancelItemsModal,
  type CancelItemsOutcome,
  type CancelItemsSelection,
} from "./manage/CancelItemsModal";
import { SplitBillModal } from "./split/SplitBillModal";
import { TransferBillFlow } from "./manage/TransferBillFlow";
import type { SecondaryAction } from "./OptionsPopover";
import { TableFooterBar } from "./footer/TableFooterBar";
import type { FooterBarItem } from "./footer/footerPinning";
import { useTableFooterShortcuts } from "./useTableFooterShortcuts";
import { ClientsModal } from "./clients/ClientsModal";
import { ItemDiscountsModal } from "./discounts/ItemDiscountsModal";
import { CustomPriceModal } from "./pricing/CustomPriceModal";
import { ReportTypeSelectionModal } from "./reports/ReportTypeSelectionModal";
import { GeneratePersonalReportModal } from "./reports/GeneratePersonalReportModal";
import { GenerateEstablishmentReportModal } from "./reports/GenerateEstablishmentReportModal";
import { OperationsModal } from "./operations/OperationsModal";
import { InvoiceBillSelectionModal } from "./invoicing/InvoiceBillSelectionModal";
import { InvoiceCreateModal } from "./invoicing/InvoiceCreateModal";
import { InvoicePreviewModal } from "./invoicing/InvoicePreviewModal";
import { PrintPosConfirmModal } from "./reports/PrintPosConfirmModal";
import type { BillHistory } from "../../../../api/reportsBillsHistory";
import { printInvoicePos, type Invoice } from "../../../../api/invoices";
import { printPreliminaryBill, setBillGuests } from "../../../../api/bills";
import { useGuestCountPrompt } from "./guests/useGuestCountPrompt";
import { getApiErrorMessage } from "../../../../utils/translations";
import { useDefaultCompany } from "../../../../hooks/useCompanies";
import { RestoreBillModal } from "./manage/RestoreBillModal";
import { useSeatReservationAction } from "./seat/useSeatReservationAction";
import { ForkKnifeIcon } from "../../lunch/ForkKnifeIcon";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setSelectedBillId } from "../../../../store";
import {
  useBills,
  useCancelBillItems,
} from "../../../../hooks/useBills";
import { usePrinterNotifications } from "../../../../hooks/usePrinterNotifications";
import { useConfiguration } from "../../../../hooks/useConfiguration";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";

interface TableFooterProps {
  onNewTable?: () => void;
  onAddToTable?: () => void;
  selectedTableId: number | null;
  selectedTableName?: string | null;
  onClearSelection?: () => void;
  onRenameModalOpenChange?: (open: boolean) => void;
  onAnyModalOpenChange?: (open: boolean) => void;
  /** Бързо закриване: id на сметката, за която да се отвори модалът „Закрий"
   *  веднага щом се появи в списъка (след „Поръчай"). Родителят го чисти. */
  autoCloseBillId?: number | null;
  onAutoCloseHandled?: () => void;
  /** „Настани" (резервация) е отворил/закачил сметката — родителят я избира
   *  и влиза в менюто за маркиране. */
  onReservationSeated?: (billId: number) => void;
}

export const TableFooter = ({
  onNewTable,
  onAddToTable,
  selectedTableId,
  selectedTableName,
  onClearSelection,
  onRenameModalOpenChange,
  onAnyModalOpenChange,
  autoCloseBillId,
  onAutoCloseHandled,
  onReservationSeated,
}: TableFooterProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.auth.permissions);
  // „Закриване веднага след поръчка": при нея модалът „Закрий" изскача преди
  // сервитьорът да е имал шанс да мине през „Опции → Клиенти" — затова там
  // получава собствен бутон „Клиенти" (само с правото select_customer).
  const { configuration } = useConfiguration();
  const quickCloseEnabled = configuration.orderingQuickClose;
  // Локален режим (docs/edge.md): действията извън обхвата на касата
  // („Раздели", „Отчет", „Фактуриране", „Възстанови") остават видими, но
  // disabled с подсказка; същото важи за клавишите им. Клиенти, отстъпки,
  // промяна на цена, прехвърляне, операции и „Закрий" на закрита без бон
  // сметка (/close/retry) касата сервира — те са свободни.
  const edgeLocal = useIsEdgeLocal();
  const offlineLock = edgeLocal ? t("edge.unavailableOffline") : undefined;

  const canOpenBill = permissions.some((p) => p.name === "open_bill");
  const canVoidItem = permissions.some((p) => p.name === "void_item");
  const canCloseBill = permissions.some((p) => p.name === "close_bill");
  const canSplitBill = permissions.some((p) => p.name === "split_bill");
  const canPaymentCash = permissions.some((p) => p.name === "payment_cash");
  const canPaymentCard = permissions.some((p) => p.name === "payment_card");
  const canCloseBill1 = permissions.some((p) => p.name === "close_bill_1");
  const canSelectCustomer = permissions.some(
    (p) => p.name === "select_customer",
  );
  const canItemDiscount = permissions.some(
    (p) => p.name === "set_discount_percent",
  );
  const canSetCustomPrice = permissions.some(
    (p) => p.name === "set_custom_price",
  );
  const canPreliminaryBill = permissions.some(
    (p) => p.name === "preliminary_bill",
  );
  const canFiscalOperations = permissions.some(
    (p) => p.name === "fiscal_operations",
  );
  const canRestoreLastBill = permissions.some(
    (p) => p.name === "restore_last_bill",
  );
  const canChangePaymentMethod = permissions.some(
    (p) => p.name === "change_payment_method",
  );
  const canChangeTable = permissions.some((p) => p.name === "change_table");
  const canTransferBill = permissions.some((p) => p.name === "transfer_bill");
  const canPersonalReport = permissions.some(
    (p) => p.name === "personal_report",
  );
  const canInvoicing = permissions.some((p) => p.name === "invoicing");
  const canEstablishmentReport = permissions.some(
    (p) => p.name === "establishment_report",
  );
  const canAnyReport = canPersonalReport || canEstablishmentReport;
  const canManageLunchMenu = permissions.some((p) => p.name === "manage_lunch_menu");
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [isItemDiscountModalOpen, setIsItemDiscountModalOpen] = useState(false);
  const [isCustomPriceModalOpen, setIsCustomPriceModalOpen] = useState(false);
  const [isReportTypeModalOpen, setIsReportTypeModalOpen] = useState(false);
  const [isPersonalReportModalOpen, setIsPersonalReportModalOpen] =
    useState(false);
  const [isEstablishmentReportModalOpen, setIsEstablishmentReportModalOpen] =
    useState(false);
  const [isOperationsModalOpen, setIsOperationsModalOpen] = useState(false);
  const [isInvoicingModalOpen, setIsInvoicingModalOpen] = useState(false);
  const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
  const [invoiceBills, setInvoiceBills] = useState<BillHistory[]>([]);
  const [invoicePreview, setInvoicePreview] = useState<Invoice | null>(null);
  // Фактурата е записана — питаме дали да излезе и на POS принтера, преди
  // да покажем A4 preview-то (същият flow като при отчетите).
  const [invoicePosAsk, setInvoicePosAsk] = useState<Invoice | null>(null);
  const [isPrintingInvoicePos, setIsPrintingInvoicePos] = useState(false);
  // Печатът на предварителна сметка минава през брокера и чака принтера —
  // на бавна серийна връзка това са секунди, през които бутонът трябва да
  // е зает, иначе нетърпеливо второ натискане пуска втори лист.
  const [isPrintingPreliminaryBill, setIsPrintingPreliminaryBill] =
    useState(false);
  // Прескачаме query, докато операторът не отвори flow-а — Default-фирмата е
  // нужна само за валидация преди фактуриране, не за всеки сесия рендер.
  const { data: defaultCompany } = useDefaultCompany({ enabled: true });
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  // Сметка от „Възстановяване", маркирана за фискализиране: отваря „Закрий"
  // (mode="retry") за нея — операторът пак избира метод на плащане. Данните
  // идват от историята, защото закрита нефискализирана сметка може изобщо да
  // не е в списъка с маси (non_fiscal_display="never").
  const [fiscalizeBill, setFiscalizeBill] = useState<{
    id: number;
    name: string;
    totalAmount: string;
  } | null>(null);
  // Новата (възстановена) сметка, за която чакаме да отворим модала „Закрий"
  // след „Смяна на начин на плащане" в модала за възстановяване.
  const [changePaymentBillId, setChangePaymentBillId] = useState<number | null>(
    null,
  );
  const [isTransferWaiterModalOpen, setIsTransferWaiterModalOpen] =
    useState(false);
  const [isRenameModalOpen, setIsRenameModalOpenState] = useState(false);
  const setIsRenameModalOpen = useCallback(
    (next: boolean) => {
      setIsRenameModalOpenState(next);
      onRenameModalOpenChange?.(next);
    },
    [onRenameModalOpenChange],
  );

  // „Брой гости" преди „Закрий" (Конфигурация → Маркиране → „Въвеждане на
  // брой гости" = преди закриване).
  const {
    askFor: askGuestCountFor,
    askGuestCount,
    guestCountModal,
    guestCountModalOpen,
  } = useGuestCountPrompt();

  // „Настани" — модалът живее в seat/ (docs/ordering.md).
  const seatReservation = useSeatReservationAction({
    onSeated: (billId) => onReservationSeated?.(billId),
  });

  // Агрегиран сигнал нагоре — дали който и да е от модалите на footer-а е отворен.
  // Родителят го подава на TableList, за да изключи глобалната клавиатурна търсачка.
  const anyFooterModalOpen =
    isModalOpen ||
    isCancelModalOpen ||
    isSplitModalOpen ||
    isClientsModalOpen ||
    isItemDiscountModalOpen ||
    isCustomPriceModalOpen ||
    isReportTypeModalOpen ||
    isPersonalReportModalOpen ||
    isEstablishmentReportModalOpen ||
    isOperationsModalOpen ||
    isInvoicingModalOpen ||
    invoiceCreateOpen ||
    isRestoreModalOpen ||
    fiscalizeBill !== null ||
    isTransferWaiterModalOpen ||
    isRenameModalOpen ||
    guestCountModalOpen ||
    seatReservation.open;
  useEffect(() => {
    onAnyModalOpenChange?.(anyFooterModalOpen);
  }, [anyFooterModalOpen, onAnyModalOpenChange]);

  const names = useAppSelector((state) => state.auth.names);
  const { data: bills = [] } = useBills();
  const cancelBillItemsMutation = useCancelBillItems();

  const selectedBill = bills.find((bill) => bill.id === selectedTableId);
  const hasSelectedBill = !!selectedBill;
  // Закрита сметка без касова бележка. Бутонът „Закрий" отваря СЪЩИЯ модал —
  // със същите методи и същия избор — само че заявката отива към
  // `/close/retry`, защото сметката вече е закрита.
  const isClosedEmpty =
    selectedBill?.status === "closed" &&
    parseFloat(selectedBill?.total || "0") === 0;
  // Закриването на избраната маса още чака принтера (кутийката ѝ се върти):
  // нито поръчка, нито второ закриване, нито друго действие по нея — като
  // докато модалът стоеше отворен със спинера (payment/backgroundBillClose.ts).
  const { closingBillIds } = useClosingBills();
  const isSelectedBillClosing =
    selectedTableId != null && closingBillIds.has(selectedTableId);
  const isActionDisabled = isClosedEmpty || isSelectedBillClosing;
  const operatorName = names || "N/A";
  const totalAmount = selectedBill?.total_amount || "0.00";

  const handleReportClick = useCallback(() => {
    if (canPersonalReport && !canEstablishmentReport) {
      setIsPersonalReportModalOpen(true);
    } else if (canEstablishmentReport && !canPersonalReport) {
      setIsEstablishmentReportModalOpen(true);
    } else {
      setIsReportTypeModalOpen(true);
    }
  }, [canPersonalReport, canEstablishmentReport]);

  // Отворена сметка при „Брой гости" преди закриване: първо броят (записва
  // се в BE още в модала), „Закрий" се отваря само след „Потвърди"; „Отказ"
  // не отваря нищо. Закрита без бон (/close/retry) не се пита.
  const askGuestsBeforeClose = askGuestCountFor("close");
  const selectedOpenBillId =
    selectedBill?.status === "open" ? selectedBill.id : null;
  const handleCloseClick = useCallback(async () => {
    if (askGuestsBeforeClose && selectedOpenBillId != null) {
      const guests = await askGuestCount({
        save: (count) => setBillGuests(selectedOpenBillId, count),
      });
      if (guests == null) return;
    }
    setIsModalOpen(true);
  }, [askGuestsBeforeClose, selectedOpenBillId, askGuestCount]);

  // Бързо закриване: щом поръчаната сметка се появи в списъка (и е избраната),
  // отваряме модала „Закрий" веднъж и сигнализираме на родителя да изчисти флага.
  useEffect(() => {
    if (autoCloseBillId == null) return;
    if (!canCloseBill) {
      onAutoCloseHandled?.();
      return;
    }
    // изчакай bills да се презареди и сметката да стане избраната
    if (selectedTableId !== autoCloseBillId || !hasSelectedBill) return;
    void handleCloseClick();
    onAutoCloseHandled?.();
  }, [
    autoCloseBillId,
    selectedTableId,
    hasSelectedBill,
    canCloseBill,
    handleCloseClick,
    onAutoCloseHandled,
  ]);

  // „Смяна на начин на плащане": restore-ът вече е минал (със сторно бон, ако
  // сметката е била фискализирана) и е създал НОВА отворена сметка. Изчакваме
  // тя да се появи в списъка и да стане избраната, и отваряме „Закрий" за нея,
  // за да се избере новият начин на плащане без ръчни стъпки.
  useEffect(() => {
    if (changePaymentBillId == null) return;
    // Операторът е избрал друга маса, докато списъкът се презарежда — отказваме
    // се, вместо да отворим „Закрий" изневиделица по-късно.
    if (selectedTableId != null && selectedTableId !== changePaymentBillId) {
      setChangePaymentBillId(null);
      return;
    }
    if (selectedTableId !== changePaymentBillId || !hasSelectedBill) return;
    void handleCloseClick();
    setChangePaymentBillId(null);
  }, [
    changePaymentBillId,
    selectedTableId,
    hasSelectedBill,
    handleCloseClick,
  ]);

  const openSplitModal = useCallback(() => setIsSplitModalOpen(true), []);

  // F5 Нова · F6 Добави · F7 Закрий · F2 Отчет · F10 Раздели
  useTableFooterShortcuts({
    canOpenBill,
    canCloseBill,
    canSplitBill,
    canAnyReport,
    hasSelectedBill,
    isActionDisabled,
    isClosing: isSelectedBillClosing,
    edgeLocal,
    onNewTable,
    onAddToTable,
    onClose: handleCloseClick,
    onReport: handleReportClick,
    onSplit: openSplitModal,
  });

  // Сторно на един или повече редове — ЕДНА заявка за всички избрани
  // (`/cancel-items`): сървърът ги сторнира заедно (всичко или нищо) и печата
  // ЕДИН кухненски билет на станция. Стар сървър без адреса → ред по ред
  // (`api/billsCancel.ts`), както преди. Пълно и частично количество.
  const handleCancelItems = async (
    data: CancelItemsSelection,
  ): Promise<CancelItemsOutcome | void> => {
    if (!selectedTableId) return;

    try {
      await cancelBillItemsMutation.mutateAsync({
        billId: selectedTableId,
        data: {
          items: data.elements.map((element) => ({
            billElementId: element.billElementId,
            billElementItemId: element.billElementItemId,
            quantity: element.quantity,
          })),
          reason: data.reason,
          isDefective: data.isDefective,
        },
      });

      if (onClearSelection) {
        onClearSelection();
      }
    } catch (err: unknown) {
      const text = getApiErrorMessage(err, t("common.error"));
      message.error(text);
      // ВРЪЩАМЕ отказа, а не само го показваме: прозорецът затваря само при
      // успех (`CancelItemsOutcome`). Без този `return` пропадналото сторно
      // изглеждаше точно като минало — прозорецът се затваряше, селекцията
      // се губеше и оставаше само едно съобщение, което си отива само.
      return { ok: false, error: text };
    }
  };


  // Предварителна сметка — печат на POS принтера на оператора. Не издава
  // и не фискализира нищо, затова е свободно повторим: при грешка се
  // натиска пак. Без диалог за потвърждение — за разлика от фактурата и
  // отчетите, тук няма какво да се обърка, а сервитьорът иска листа
  // веднага, докато е на масата.
  const handlePreliminaryBill = useCallback(async () => {
    if (!selectedTableId || isPrintingPreliminaryBill) return;
    setIsPrintingPreliminaryBill(true);
    // Popover-ът се затваря при клика, тоест бутонът вече го няма и spinner
    // върху него не би се видял. Затова прогресът е toast с фиксиран key,
    // който после се ПОДМЕНЯ от изхода — на бавна серийна връзка печатът е
    // секунди и мълчанието изглежда като „бутонът не работи".
    const key = "preliminary-bill";
    message.open({
      key,
      type: "loading",
      content: t("ordering.preliminaryBillPrinting"),
      duration: 0,
    });
    try {
      await printPreliminaryBill(selectedTableId);
      message.open({
        key,
        type: "success",
        content: t("ordering.preliminaryBillSuccess"),
      });
    } catch (error: unknown) {
      // Loading toast-ът се маха ръчно — изходът вече не е message със същия
      // key, а постоянна принтерна нотификация с ръчно затваряне.
      message.destroy(key);
      notifyPrinterError(
        getApiErrorMessage(error, t("ordering.preliminaryBillError")),
      );
    } finally {
      setIsPrintingPreliminaryBill(false);
    }
  }, [
    selectedTableId,
    isPrintingPreliminaryBill,
    message,
    notifyPrinterError,
    t,
  ]);

  // Вторичните действия събираме в списък — стоят в „Опции“, а довлечените от
  // потребителя и в лентата (footer/TableFooterBar); `key` е записаният ключ в
  // личната настройка footerActions — не го преименувай, иначе бутонът се връща в „Опции“.
  const secondaryActions = [
    canVoidItem && {
      key: "cancel",
      label: t("ordering.cancelItem"),
      icon: <MinusCircleOutlined />,
      // Сторното вече не изисква избран продукт — модалът показва всички
      // поръчани редове и в него се избира кои/колко да се сторнират.
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsCancelModalOpen(true),
    },
    canSplitBill && {
      key: "split",
      lockedTooltip: offlineLock,
      label: t("ordering.split"),
      icon: <ScissorOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsSplitModalOpen(true),
      shortcut: "F10",
    },
    canChangeTable && {
      key: "rename",
      label: t("ordering.renameTable"),
      icon: <EditOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsRenameModalOpen(true),
    },
    canTransferBill && {
      key: "transferWaiter",
      label: t("ordering.transferBill"),
      icon: <UserSwitchOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsTransferWaiterModalOpen(true),
    },
    canSelectCustomer && {
      key: "clients",
      label: t("ordering.clients"),
      icon: <TeamOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      // Заключен ред НЕ блокира избора на клиент — отстъпката е на ниво
      // сметка и BE така или иначе прескача редовете със заключена цена.
      onClick: () => setIsClientsModalOpen(true),
    },
    canItemDiscount && {
      key: "discounts",
      label: t("ordering.discounts"),
      icon: <PercentageOutlined />,
      // Не иска избран продукт — модалът показва всички редове на сметката
      // с общ процент и избор на бройки по ред.
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsItemDiscountModalOpen(true),
    },
    canSetCustomPrice && {
      key: "customPrice",
      // Не иска избран продукт — модалът показва всички редове на сметката
      // (продукти и добавки), всеки със собствен молив за нова цена.
      label: t("ordering.customPrice"),
      icon: <TagOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsCustomPriceModalOpen(true),
    },
    canPreliminaryBill && {
      key: "preliminaryBill",
      label: t("ordering.preliminaryBill"),
      icon: <PrinterOutlined />,
      // Затворена сметка вече има фискален бон — BE-то връща 409, но по-добре
      // бутонът да е сив, отколкото натискането да върне грешка.
      disabled:
        !hasSelectedBill ||
        isActionDisabled ||
        selectedBill?.status !== "open" ||
        isPrintingPreliminaryBill,
      onClick: handlePreliminaryBill,
    },
    canAnyReport && {
      key: "report",
      lockedTooltip: offlineLock,
      label: t("ordering.report"),
      icon: <FileTextOutlined />,
      onClick: handleReportClick,
      shortcut: "F2",
    },
    canFiscalOperations && {
      key: "operations",
      label: t("ordering.operations"),
      icon: <ToolOutlined />,
      onClick: () => setIsOperationsModalOpen(true),
    },
    canInvoicing && {
      key: "invoicing",
      lockedTooltip: offlineLock,
      label: t("ordering.invoicing"),
      icon: <FileDoneOutlined />,
      onClick: () => {
        if (!defaultCompany) {
          // Без текуща фирма не можем да издаваме фактура. Спираме рано —
          // отварянето на модала би имало смисъл само да каже „няма фирма",
          // което toast-ът прави по-бързо.
          message.error(t("invoicing.needDefaultCompany"));
          return;
        }
        setIsInvoicingModalOpen(true);
      },
    },
    seatReservation.action,
    // „Обедно меню" — отделният екран /ordering/lunch-menu; извън обхвата на
    // касата → заключен в локален режим.
    canManageLunchMenu && {
      key: "lunchMenu",
      lockedTooltip: offlineLock,
      label: t("lunchMenu.title"),
      icon: <ForkKnifeIcon />,
      onClick: () => navigate("/ordering/lunch-menu"),
    },
    canRestoreLastBill && {
      key: "restore",
      lockedTooltip: offlineLock,
      label: t("ordering.restore"),
      icon: <UndoOutlined />,
      onClick: () => setIsRestoreModalOpen(true),
    },
  ].filter(Boolean) as SecondaryAction[];

  // Основните бутони в обичайния им ред; ключовете им (`MAIN_FOOTER_KEYS`)
  // са в личната подредба на лентата — не ги преименувай.
  const mainButtons = [
    canOpenBill && {
      key: "newTable",
      button: {
        icon: <PlusOutlined />,
        label: t("ordering.newTable"),
        onClick: onNewTable,
        shortcut: "F5",
      },
    },
    canOpenBill && {
      key: "addToTable",
      button: {
        icon: <ShoppingCartOutlined />,
        label: t("ordering.add"),
        onClick: onAddToTable,
        disabled: !hasSelectedBill || isActionDisabled,
        variant: "primary",
        shortcut: "F6",
      },
    },
    canCloseBill && {
      key: "closeTable",
      button: {
        icon: <CheckOutlined />,
        label: t("ordering.closeTable"),
        onClick: handleCloseClick,
        disabled: !hasSelectedBill || isSelectedBillClosing,
        variant: "danger",
        shortcut: "F7",
        loading: isSelectedBillClosing,
      },
    },
  ].filter(Boolean) as FooterBarItem[];

  return (
    <>
      <ReportTypeSelectionModal
        open={isReportTypeModalOpen}
        onClose={() => setIsReportTypeModalOpen(false)}
        onSelectPersonal={() => {
          setIsReportTypeModalOpen(false);
          setIsPersonalReportModalOpen(true);
        }}
        onSelectEstablishment={() => {
          setIsReportTypeModalOpen(false);
          setIsEstablishmentReportModalOpen(true);
        }}
      />

      <GeneratePersonalReportModal
        open={isPersonalReportModalOpen}
        onClose={() => setIsPersonalReportModalOpen(false)}
      />

      <GenerateEstablishmentReportModal
        open={isEstablishmentReportModalOpen}
        onClose={() => setIsEstablishmentReportModalOpen(false)}
      />

      <OperationsModal
        open={isOperationsModalOpen}
        onClose={() => setIsOperationsModalOpen(false)}
      />

      <InvoiceBillSelectionModal
        open={isInvoicingModalOpen}
        onClose={() => setIsInvoicingModalOpen(false)}
        onConfirm={(bills) => {
          setInvoiceBills(bills);
          setIsInvoicingModalOpen(false);
          setInvoiceCreateOpen(true);
        }}
      />

      <InvoiceCreateModal
        open={invoiceCreateOpen}
        bills={invoiceBills}
        onClose={() => setInvoiceCreateOpen(false)}
        onSaved={(invoice) => {
          setInvoiceCreateOpen(false);
          setInvoicePosAsk(invoice);
        }}
      />

      <PrintPosConfirmModal
        open={invoicePosAsk !== null}
        loading={isPrintingInvoicePos}
        title={t("invoicePreview.printPosConfirmTitle")}
        subtitle={t("invoicePreview.printPosConfirmDescription")}
        onYes={async () => {
          const invoice = invoicePosAsk;
          if (!invoice) return;
          setIsPrintingInvoicePos(true);
          try {
            await printInvoicePos(invoice.id);
            message.success(t("invoicePreview.printPosSuccess"));
          } catch (error: unknown) {
            notifyPrinterError(
              getApiErrorMessage(error, t("invoicePreview.printPosError")),
            );
          } finally {
            setIsPrintingInvoicePos(false);
            setInvoicePosAsk(null);
            // A4 preview-то се отваря и при неуспешен печат — операторът
            // не губи достъпа до документа заради мълчащ принтер.
            setInvoicePreview(invoice);
          }
        }}
        onNo={() => {
          const invoice = invoicePosAsk;
          setInvoicePosAsk(null);
          setInvoicePreview(invoice);
        }}
      />

      <InvoicePreviewModal
        open={invoicePreview !== null}
        invoice={invoicePreview}
        onClose={() => setInvoicePreview(null)}
      />

      <RestoreBillModal
        open={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        canChangePaymentMethod={canChangePaymentMethod}
        onRestoredForPaymentChange={(billId) => {
          dispatch(setSelectedBillId(billId));
          setChangePaymentBillId(billId);
        }}
        onFiscalize={canCloseBill ? setFiscalizeBill : undefined}
      />

      {/* „Фискализирай" от „Възстановяване": същият модал „Закрий" като при
          closed-empty маса, само че за сметка от историята. */}
      {fiscalizeBill && (
        <CloseTableModal
          open
          onClose={() => setFiscalizeBill(null)}
          billId={fiscalizeBill.id}
          tableName={fiscalizeBill.name}
          totalAmount={fiscalizeBill.totalAmount}
          operatorName={operatorName}
          canPaymentCash={canPaymentCash}
          canPaymentCard={canPaymentCard}
          canCloseBill1={canCloseBill1}
          mode="retry"
          // F7 отваря и „Закрий" за избраната маса ВЪРХУ този модал (F-клави-
          // шите не гледат отворени модали) — двата слушат Enter на window,
          // затова при двойно отваряне клавиатурата се изключва и в двата.
          keyboardDisabled={isModalOpen}
        />
      )}

      {selectedTableId && (
        <>
          <CloseTableModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            billId={selectedTableId}
            tableName={selectedTableName || "N/A"}
            totalAmount={totalAmount}
            operatorName={operatorName}
            canPaymentCash={canPaymentCash}
            canPaymentCard={canPaymentCard}
            canCloseBill1={canCloseBill1}
            customerId={selectedBill?.customer_id ?? null}
            bill={selectedBill ?? null}
            mode={isClosedEmpty ? "retry" : "close"}
            onOpenClients={
              quickCloseEnabled && canSelectCustomer
                ? () => setIsClientsModalOpen(true)
                : undefined
            }
            keyboardDisabled={isClientsModalOpen || fiscalizeBill !== null}
          />
          <CancelItemsModal
            open={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            onConfirm={handleCancelItems}
            bill={selectedBill}
          />
          <SplitBillModal
            open={isSplitModalOpen}
            onClose={() => setIsSplitModalOpen(false)}
            billId={selectedTableId}
            bill={selectedBill}
          />
          <RenameTableModal
            open={isRenameModalOpen}
            billId={selectedTableId}
            currentName={selectedTableName || ""}
            onClose={() => setIsRenameModalOpen(false)}
          />
          <TransferBillFlow
            open={isTransferWaiterModalOpen}
            onClose={() => setIsTransferWaiterModalOpen(false)}
            billId={selectedTableId}
            tableName={selectedTableName || ""}
            currentWaiterId={selectedBill?.waiter_id ?? null}
            totalAmount={totalAmount}
          />
        </>
      )}

      {seatReservation.modal}

      {/* „Брой гости" преди „Закрий" */}
      {guestCountModal}

      <ClientsModal
        open={isClientsModalOpen}
        onClose={() => setIsClientsModalOpen(false)}
        billId={selectedTableId}
      />

      <ItemDiscountsModal
        open={isItemDiscountModalOpen}
        onClose={() => setIsItemDiscountModalOpen(false)}
        billId={selectedTableId}
        bill={selectedBill}
      />

      <CustomPriceModal
        open={isCustomPriceModalOpen}
        onClose={() => setIsCustomPriceModalOpen(false)}
        billId={selectedTableId}
        bill={selectedBill}
      />

      {/* Лентата (Нова · Добави · Закрий · изнесените от „Опции" · Опции) и
          влаченето между „Опции" и лентата — footer/TableFooterBar */}
      <TableFooterBar
        mainButtons={mainButtons}
        secondaryActions={secondaryActions}
      />
    </>
  );
};
